// apps/web/src/features/agent/AgentArtifactsPane.tsx
//
// Right-pane artifacts area (#1876). Renders each streamed artifact via the
// typed per-kind renderer (#1877): kpi/table/text cards, with chart and
// unknown kinds routed to a plain-text unsupported notice. Each artifact sits
// in a titled Card; the section keeps its region label, title, and empty
// state.
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import type { AgentRenderableArtifact } from '@magimp/shared';
import { Card, EmptyState } from '../../ui/index.js';
import { ArtifactRenderer } from './artifacts/index.js';

export interface AgentArtifactsPaneProps {
  artifacts: AgentRenderableArtifact[];
  renderArtifactAction?: (artifact: AgentRenderableArtifact) => ReactNode;
}

export function AgentArtifactsPane({ artifacts, renderArtifactAction }: AgentArtifactsPaneProps) {
  const { t } = useTranslation('agent');

  return (
    <section
      aria-label={t('artifacts.regionLabel')}
      data-testid="agent-artifacts-pane"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-3)',
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <h2 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: 'var(--fg-2)' }}>
        {t('artifacts.title')}
      </h2>
      {artifacts.length === 0 ? (
        <EmptyState icon="bar-chart-3" text={t('artifacts.empty')} />
      ) : (
        artifacts.map((artifact) => (
          <Card
            key={artifact.id}
            style={{
              padding: 'var(--s-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--s-3)',
            }}
          >
            {renderArtifactAction !== undefined ? (
              <div style={{ alignSelf: 'flex-end' }}>{renderArtifactAction(artifact)}</div>
            ) : null}
            <ArtifactRenderer artifact={artifact} />
          </Card>
        ))
      )}
    </section>
  );
}
